import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Platform,
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
} from 'react-native';
import Svg, { Path, Line, Circle } from 'react-native-svg';
import LocationAdminTable, { AdminTableColumn } from './LocationManagement/components/LocationAdminTable';
import { AdminColors } from '../constants/adminColors';
import AdminBreadcrumb from '../components/AdminBreadcrumb';
import AdminButton from '../components/AdminButton';
import AdminInfoBanner from '../components/AdminInfoBanner';
import AdminSelect, { SelectOption } from '../components/AdminSelect';
import FontFamily from '../../../constants/fonts';
// LocationStatusMenu is now built into LocationAdminTable
import LocationManagementModal, { LocationModalMode } from './LocationManagement/LocationManagementModal';
import RevertModal from '../components/RevertModal';
import LocationDetail from './LocationManagement/LocationDetail';
import MapLibreMap, { MapMarker } from '../../../components/MapLibreMap/MapLibreMap';
import WorkspaceViewBar, { WorkspaceView } from './LocationManagement/components/WorkspaceViewBar';
import LocationMapView from './LocationManagement/LocationMapView';
import LocationFilterBar from './LocationManagement/components/LocationFilterBar';
import MoreAttributesButton from './LocationManagement/components/MoreAttributesButton';
import { FilterOption } from './LocationManagement/components/LocationFilterDropdown';
import MoreAttributesDrawer, {
  createEmptyMoreAttributesFilters,
  getMoreAttributesActiveCount,
  MoreAttributesFilterState,
} from './LocationManagement/components/MoreAttributesDrawer';
import GroupingBar, { GroupingState } from './LocationManagement/components/GroupingBar';
import SelectionBar from './LocationManagement/components/SelectionBar';
import SummaryStrip from './LocationManagement/components/SummaryStrip';
import LocationTableToolbar from './LocationManagement/components/LocationTableToolbar';
import EdfSyncBanner from './LocationManagement/components/EdfSyncBanner';
import { ALL_COLUMNS } from './LocationManagement/components/ColumnPickerModal';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store';
import {
  selectLocTypes,
  selectOrgTypes,
  selectLocationFacilityTypes,
} from '../../../store/slices/masterDataSlice';

import {
  Location,
  FilterState,
  DEFAULT_FILTERS,
  PagedResult,
} from './LocationManagement/locationTypes';
import { locationApi, LocationFilterField, LocationFilterOptionsResponse } from './LocationManagement/locationApi';
import { createApiClient, apiClient } from '../../../api/apiClient';
import { useDispatch } from 'react-redux';
import { updateLocationInState } from '../../../store/slices/appDataSlice';
import { useToast } from '../../../components/LhToastContainer';
import ExcelJS from 'exceljs';
import { downloadWorkbook } from '../../AnalyticsHub/utils/workbookDownload';
import { useTheme } from '../../../theme/useTheme';

type SortDirection = 'asc' | 'desc' | 'default';

const LOCATION_SORT_FIELD_MAP: Record<string, string> = {
  id: 'locId',
  sap: 'locSapId',
  name: 'locName',
  facilityType: 'locationFacilityType',
  type: 'locationType',
  org: 'organizationType',
  tpl: 'is3pl',
  size: 'size',
  region: 'region',
  market: 'market',
  marketLead: 'marketLead',
  regionLead: 'regionLead',
  coe: 'coeResource',
  coe2: 'secondaryCoeResource',
  executionSystem: 'executionSystem',
  warehouseSystem: 'warehouseSystem',
  warehouseStartTime: 'warehouseStartTime',
  labor: 'laborModel',
  union: 'unionStatus',
  supplier: 'tplSupplier',
  ownership: 'ownership',
  yearBuilt: 'yearBuilt',
  address: 'streetAddress',
  city: 'city',
  state: 'stateProvince',
  zip: 'postalCode',
  latitude: 'latitude',
  longitude: 'longitude',
  totalSqft: 'totalBuildingSqft',
  operationalSqft: 'operationalSqft',
  docks: 'dockCount',
  docksEng: 'docksWithENGControls',
  docksNoEng: 'docksWithoutENGControls',
  driveIn: 'driveInBays',
  bottleLines: 'bottleLines',
  canLines: 'canLines',
  build1: 'crossdockBuildSite1',
  build2: 'crossdockBuildSite2',
  bulkSite: 'centralizedBulkSite',
  status: 'status',
};

const toApiSortDirection = (direction?: SortDirection) => {
  if (!direction) return undefined;
  return direction.toUpperCase() as 'ASC' | 'DESC' | 'DEFAULT';
};

const SHOW_BY_COLOR_PALETTE = [
  '#2A92C6',
  '#0F4E67',
  '#C65295',
  '#7B1E5A',
  '#1BA21C',
  '#0F5313',
  '#D57A00',
  '#8A3B0F',
  '#746BD9',
  '#4333A2',
  '#E94B1E',
  '#8A2307',
];

const ROUND_ROBIN_COLOR_ASSIGNMENTS = new Map<string, string>();
let roundRobinColorIndex = 0;

const buildColorMap = (values: string[], palette: string[] = SHOW_BY_COLOR_PALETTE): Record<string, string> =>
  values.reduce<Record<string, string>>((acc, value, index) => {
    acc[value] = palette[index % palette.length];
    return acc;
  }, {});

const pickPaletteColor = (value: string, palette: string[] = SHOW_BY_COLOR_PALETTE): string => {
  if (!value) return palette[0];

  const existingColor = ROUND_ROBIN_COLOR_ASSIGNMENTS.get(value);
  if (existingColor) return existingColor;

  const nextColor = palette[roundRobinColorIndex % palette.length];
  ROUND_ROBIN_COLOR_ASSIGNMENTS.set(value, nextColor);
  roundRobinColorIndex += 1;
  return nextColor;
};


const PlusIcon = () => (
  <Svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={AdminColors.textOnDark} strokeWidth="2">
    <Line x1="12" y1="5" x2="12" y2="19" />
    <Line x1="5" y1="12" x2="19" y2="12" />
  </Svg>
);

// Main screen 
type View_ = 'list' | 'detail';

interface LocationManagementScreenProps {
  onBack: () => void;
  canEditLocations?: boolean;
}

const DEFAULT_VISIBLE_COLUMNS = [
  'id',
  'sap',
  'name',
  'status',
  'facilityType',
  'type',
  'org',
  'tpl',
  'size',
  'region',
  'market',
  'marketLead',
  'coe',
  'coe2',
];

const DEFAULT_GROUPING: GroupingState = {
  preset: 'flat',
  levels: [],
  allExpanded: false,
};

const toFilterOptions = (values: string[]) => values.map(value => ({ id: value, displayText: value }));

const toNumber = (value: string | undefined): number | null => {
  if (value == null || value.trim() === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const matchesRange = (value: string | undefined, min: string, max: string): boolean => {
  if (min.trim() === '' && max.trim() === '') {
    return true;
  }

  const parsed = toNumber(value);
  if (parsed == null) {
    return false;
  }

  const lower = toNumber(min);
  const upper = toNumber(max);
  if (lower != null && parsed < lower) return false;
  if (upper != null && parsed > upper) return false;
  return true;
};

const matchesSelection = (value: string | undefined, selected: string[]): boolean => {
  if (selected.length === 0) return true;

  const normalize = (input: string) => input.trim().toLowerCase();
  const normalizedValue = normalize(value ?? '');
  return selected.some(item => normalize(item) === normalizedValue);
};

const matchesText = (value: string | undefined, query: string): boolean => {
  const normalizedQuery = query.trim().toLowerCase();
  if (normalizedQuery === '') return true;
  return (value ?? '').toLowerCase().includes(normalizedQuery);
};

function applyMoreAttributesFilters(locations: Location[], filters: MoreAttributesFilterState): Location[] {
  return locations.filter(location =>
    matchesSelection(location.ownership, filters.ownership) &&
    matchesSelection(location.coe, filters.coeResource) &&
    matchesSelection(location.coe2, filters.secondaryCoeResource) &&
    matchesSelection(location.regionLead, filters.regionLead) &&
    matchesSelection(location.state ?? '', filters.stateProvince) &&
    matchesSelection(location.warehouseSystem, filters.warehouseSystem) &&
    matchesSelection(location.labor, filters.labor) &&
    matchesSelection(location.union, filters.union) &&
    matchesSelection(location.supplier, filters.supplier) &&
    matchesSelection(location.isDuo ? 'true' : 'false', filters.isDuo) &&
    matchesRange(location.yearBuilt, filters.yearBuiltMin, filters.yearBuiltMax) &&
    matchesRange((location as any).pickedVolume, filters.pickedVolumeMin, filters.pickedVolumeMax) &&
    matchesRange(location.totalSqft, filters.totalSqftMin, filters.totalSqftMax) &&
    matchesRange(location.operationalSqft, filters.operationalSqftMin, filters.operationalSqftMax) &&
    matchesRange(location.docks, filters.docksMin, filters.docksMax) &&
    matchesRange(location.docksEng, filters.docksEngMin, filters.docksEngMax) &&
    matchesRange(location.docksNoEng, filters.docksNoEngMin, filters.docksNoEngMax) &&
    matchesRange(location.driveIn, filters.driveInMin, filters.driveInMax) &&
    matchesRange(location.bottleLines, filters.bottleLinesMin, filters.bottleLinesMax) &&
    matchesRange(location.canLines, filters.canLinesMin, filters.canLinesMax) &&
    matchesSelection((location as any).build1, filters.build1) &&
    matchesSelection((location as any).build2, filters.build2) &&
    matchesSelection((location as any).bulkSite, filters.bulkSite)
  );
}

const LocationManagementScreen: React.FC<LocationManagementScreenProps> = ({ onBack, canEditLocations = true }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

  const apiRef = useRef(createApiClient());
  const dispatch = useDispatch();
  const toast = useToast();
  const reduxLocTypes = useSelector((state: RootState) => selectLocTypes(state));
  const reduxOrgTypes = useSelector((state: RootState) => selectOrgTypes(state));
  const reduxLocationFacilityTypes = useSelector((state: RootState) => selectLocationFacilityTypes(state));
  const [view, setView] = useState<View_>('list');
  const [selectedLoc, setSelectedLoc] = useState<Location | null>(null);
  const [showSyncBanner, setShowSyncBanner] = useState(true);
  const [showRevert, setShowRevert] = useState(false);
  const [locationModalMode, setLocationModalMode] = useState<LocationModalMode | null>(null);
  const [revertLoc, setRevertLoc] = useState<Location | null>(null);
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState(DEFAULT_FILTERS.search);
  const [showMoreAttributes, setShowMoreAttributes] = useState(false);
  const [moreAttributes, setMoreAttributes] = useState<MoreAttributesFilterState>(createEmptyMoreAttributesFilters());
  const [appliedMoreAttributes, setAppliedMoreAttributes] = useState<MoreAttributesFilterState>(createEmptyMoreAttributesFilters());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(500);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortKey, setSortKey] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<SortDirection>('default');
  const [locationUsers, setLocationUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // New workspace state
  const [workspaceView, setWorkspaceView] = useState<WorkspaceView>('table');
  const [grouping, setGrouping] = useState<GroupingState>(DEFAULT_GROUPING);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE_COLUMNS);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  // Map view state
  const [showByAttribute, setShowByAttribute] = useState<string>('facilityType');
  const [dataLabelAttribute, setDataLabelAttribute] = useState<string>('name');
  const [mapFocusLocationId, setMapFocusLocationId] = useState<string | null>(null);

  const [pagedResult, setPagedResult] = useState<PagedResult>({
    items: [], totalElements: 0, totalPages: 0, page: 0, size: 500,
  });
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [regionOptions, setRegionOptions] = useState<string[]>([]);
  const [marketOptions, setMarketOptions] = useState<string[]>([]);
  const [locationOptions, setLocationOptions] = useState<FilterOption[]>([]);
  const [loadingLocationOptions, setLoadingLocationOptions] = useState(false);
  const [moreAttributeOptions, setMoreAttributeOptions] = useState<LocationFilterOptionsResponse>({
    stateProvince: [],
    ownership: [],
    warehouseSystem: [],
    laborModel: [],
    tplSupplier: [],
    regionLead: [],
    crossdockBuildSite1: [],
    crossdockBuildSite2: [],
    centralizedBulkSite: [],
  });
  const [loadingFilterOptions, setLoadingFilterOptions] = useState(false);
  const [loadingMarketOptions, setLoadingMarketOptions] = useState(false);

  const createMoreAttributeSearchOptions = useCallback(
    (field: LocationFilterField) => async (searchText: string) => {
      const result = await locationApi.searchFilterOptions(field, searchText);
      return result.values.map(value => ({ id: value, displayText: value }));
    },
    []
  );

  // Fetch from backend with current filters + page
  const fetchLocations = useCallback(async (f: FilterState, p: number, size: number, sort?: string, direction?: SortDirection, moreAttrs?: MoreAttributesFilterState) => {
    setLoading(true);
    setFetchError(null);
    try {
      const result = f.search.trim()
        ? await locationApi.search(f.search.trim(), f, p, size, sort, toApiSortDirection(direction), moreAttrs)
        : await locationApi.getAll(f, p, size, sort, toApiSortDirection(direction), moreAttrs);
      setPagedResult(result);
    } catch (e: any) {
      setFetchError(e.message ?? 'Failed to load locations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setFilters(prev => (prev.search === searchInput ? prev : { ...prev, search: searchInput }));
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchInput]);

  // Load region options and then resolve market options from the selected regions.
  useEffect(() => {
    const loadFilterOptions = async () => {
      setLoadingFilterOptions(true);
      try {
        const [regions, moreAttributeFilterOptions] = await Promise.all([
          locationApi.getRegions().catch(() => []),
          locationApi.getFilterOptions().catch(() => ({
            stateProvince: [],
            ownership: [],
            warehouseSystem: [],
            laborModel: [],
            tplSupplier: [],
            regionLead: [],
            crossdockBuildSite1: [],
            crossdockBuildSite2: [],
            centralizedBulkSite: [],
          })),
        ]);
        setRegionOptions(regions.sort());
        setMoreAttributeOptions(moreAttributeFilterOptions);
      } catch (e) {
        console.error('Failed to load filter options:', e);
      } finally {
        setLoadingFilterOptions(false);
      }
    };
    loadFilterOptions();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadMarketOptions = async () => {
      setLoadingMarketOptions(true);

      try {
        const markets = filters.region.length > 0
          ? await locationApi.getMarketsByRegion(filters.region)
          : await locationApi.getMarkets();

        if (cancelled) return;

        const nextMarketOptions = Array.from(new Set(markets)).sort();
        setMarketOptions(nextMarketOptions);

        setFilters(prev => {
          if (prev.market.length === 0) return prev;

          const allowedMarkets = new Set(nextMarketOptions);
          const nextSelectedMarkets = prev.market.filter(market => allowedMarkets.has(market));

          if (nextSelectedMarkets.length === prev.market.length) return prev;

          return { ...prev, market: nextSelectedMarkets };
        });
      } catch (error) {
        if (!cancelled) {
          setMarketOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingMarketOptions(false);
        }
      }
    };

    loadMarketOptions();

    return () => {
      cancelled = true;
    };
  }, [filters.region]);

  useEffect(() => {
    let cancelled = false;

    const loadLocationOptions = async () => {
      setLoadingLocationOptions(true);

      try {
        const queryFilters: FilterState = {
          ...DEFAULT_FILTERS,
          region: filters.region,
          market: filters.market,
        };

        const pageSize = 500;
        const firstPage = await locationApi.getAll(queryFilters, 0, pageSize);
        if (cancelled) return;

        const locationNameSet = new Set(
          firstPage.items
            .map(location => location.name)
            .filter((value): value is string => !!value && value.trim() !== '')
        );

        const remainingPageRequests = Array.from({ length: Math.max(firstPage.totalPages - 1, 0) }, (_, index) =>
          locationApi.getAll(queryFilters, index + 1, pageSize)
        );

        if (remainingPageRequests.length > 0) {
          const remainingPages = await Promise.all(remainingPageRequests);
          if (cancelled) return;

          remainingPages.forEach(pageResult => {
            pageResult.items.forEach(location => {
              if (location.name && location.name.trim() !== '') {
                locationNameSet.add(location.name);
              }
            });
          });
        }

        setLocationOptions(
          Array.from(locationNameSet)
            .sort()
            .map(value => ({ id: value, displayText: value }))
        );

        setFilters(prev => {
          if (prev.locationNames.length === 0) return prev;

          const allowedLocationNames = new Set(locationNameSet);
          const nextLocationNames = prev.locationNames.filter(locationName => allowedLocationNames.has(locationName));

          if (nextLocationNames.length === prev.locationNames.length) return prev;

          return { ...prev, locationNames: nextLocationNames };
        });
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load location filter options:', error);
          setLocationOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingLocationOptions(false);
        }
      }
    };

    loadLocationOptions();

    return () => {
      cancelled = true;
    };
  }, [filters.market, filters.region]);

  // Refetch whenever filters, page, pageSize, or sort changes
  useEffect(() => {
    fetchLocations(filters, page, pageSize, sortBy, sortDirection, appliedMoreAttributes);
  }, [appliedMoreAttributes, fetchLocations, filters, page, pageSize, sortBy, sortDirection]);

  // Reset to page 0 when filters change (separate effect so page change doesn't double-fire)
  const isFirstRender = React.useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    setPage(0);
  }, [filters]);

  // Derived data from server response
  const allLocations = pagedResult.items;
  const locations = useMemo(() => applyMoreAttributesFilters(allLocations, appliedMoreAttributes), [allLocations, appliedMoreAttributes]);
  const selectedLocationsInView = useMemo(
    () => locations.filter(location => selectedRows.has(location.id)),
    [locations, selectedRows]
  );
  const selectedVisibleCount = selectedLocationsInView.length;
  const moreAttributesCount = useMemo(() => getMoreAttributesActiveCount(appliedMoreAttributes), [appliedMoreAttributes]);
  const ownershipOptions = useMemo<FilterOption[]>(() =>
    moreAttributeOptions.ownership.map(value => ({ id: value, displayText: value })),
    [moreAttributeOptions.ownership]
  );
  const coeResourceOptions = useMemo<FilterOption[]>(() => Array.from(
    new Set(allLocations.map(loc => loc.coe).filter((value): value is string => !!value))
  ).sort().map(value => ({ id: value, displayText: value })), [allLocations]);
  const secondaryCoeResourceOptions = useMemo<FilterOption[]>(() => Array.from(
    new Set(allLocations.map(loc => loc.coe2).filter((value): value is string => !!value))
  ).sort().map(value => ({ id: value, displayText: value })), [allLocations]);
  const regionLeadOptions = useMemo<FilterOption[]>(() =>
    moreAttributeOptions.regionLead.map(value => ({ id: value, displayText: value })),
    [moreAttributeOptions.regionLead]
  );
  const stateProvinceOptions = useMemo<FilterOption[]>(() =>
    moreAttributeOptions.stateProvince.map(value => ({ id: value, displayText: value })),
    [moreAttributeOptions.stateProvince]
  );
  const warehouseSystemOptions = useMemo<FilterOption[]>(() =>
    moreAttributeOptions.warehouseSystem.map(value => ({ id: value, displayText: value })),
    [moreAttributeOptions.warehouseSystem]
  );
  const laborOptions = useMemo<FilterOption[]>(() =>
    moreAttributeOptions.laborModel.map(value => ({ id: value, displayText: value })),
    [moreAttributeOptions.laborModel]
  );
  const supplierOptions = useMemo<FilterOption[]>(() =>
    moreAttributeOptions.tplSupplier.map(value => ({ id: value, displayText: value })),
    [moreAttributeOptions.tplSupplier]
  );
  const buildSite1Options = useMemo<FilterOption[]>(() =>
    moreAttributeOptions.crossdockBuildSite1.map(value => ({ id: value, displayText: value })),
    [moreAttributeOptions.crossdockBuildSite1]
  );
  const buildSite2Options = useMemo<FilterOption[]>(() =>
    moreAttributeOptions.crossdockBuildSite2.map(value => ({ id: value, displayText: value })),
    [moreAttributeOptions.crossdockBuildSite2]
  );
  const bulkSiteOptions = useMemo<FilterOption[]>(() =>
    moreAttributeOptions.centralizedBulkSite.map(value => ({ id: value, displayText: value })),
    [moreAttributeOptions.centralizedBulkSite]
  );

  const requireReviewCount = useMemo(() =>
    locations.filter(l => l.status === 'Pending Activation' || l.status === 'Marked for Removal').length,
    [locations]
  );

  const hasActiveFilters = useMemo(() =>
    filters.search !== '' || filters.type.length > 0 || filters.facilityType.length > 0 || filters.org.length > 0 ||
    filters.tpl.length > 0 || filters.unionStatus.length > 0 || filters.region.length > 0 || filters.market.length > 0 ||
    filters.locationNames.length > 0 || filters.size.length > 0 || filters.status.length > 0 || moreAttributesCount > 0,
    [filters, moreAttributesCount]
  );

  const activeCount   = useMemo(() => locations.filter(l => l.status === 'Active').length,             [locations]);
  const pendingCount  = useMemo(() => locations.filter(l => l.status === 'Pending Activation').length, [locations]);
  const removalCount  = useMemo(() => locations.filter(l => l.status === 'Marked for Removal').length, [locations]);
  const disabledCount = useMemo(() => locations.filter(l => l.status === 'Disabled').length,           [locations]);
  const regionColorMap = useMemo(() => buildColorMap(regionOptions), [regionOptions]);
  const marketColorMap = useMemo(() => buildColorMap(marketOptions), [marketOptions]);

  // Helper: Get color value from location based on selected attribute
  const getColorForAttribute = useCallback((loc: Location, attr: string): string => {
    const colorMaps: Record<string, Record<string, string>> = {
      status: {
        'Active': '#004895',
        'Pending Activation': '#995211',
        'Marked for Removal': '#C12C01',
        'Disabled': '#616161',
      },
      facilityType: {
        'Cross Dock': '#2A92C6',
        'Drop Lot': '#0F4E67',
        'Unassigned': '#C65295',
        '': '#616161',
      },
      union: {
        'Non-Union': '#2A92C6',
        'Union': '#0F4E67',
        'Unknown': '#C65295',
        '': '#616161',
      },
      tpl: {
        'Yes': '#FF8C09',
        'No': '#005CBC',
        '': '#616161',
      },
      warehouseSystem: {
        'SAP': '#2A92C6',
        'Oracle': '#0F4E67',
        'AS400': '#C65295',
        '': '#616161',
      },
      ownership: {
        'Owned': '#2A92C6',
        'Leased': '#0F4E67',
        '': '#616161',
      },
      labor: {
        'Union': '#2A92C6',
        'Non-Union': '#0F4E67',
        '': '#616161',
      },
      region: regionColorMap,
      market: marketColorMap,
    };

    const map = colorMaps[attr];
    if (!map) return '#004895';
    const val = (loc as any)[attr] ?? '';
    return map[val] ?? pickPaletteColor(String(val));
  }, [marketColorMap, regionColorMap]);

  // Helper: Get label value from location based on selected attribute
  const getLabelForAttribute = useCallback((loc: Location, attr: string): string => {
    if (attr === 'none') return '';

    const value = (loc as unknown as Record<string, unknown>)[attr];
    if (value == null || value === '') {
      const fallbackLabels: Record<string, string> = {
        id: '—',
        sap: '—',
        name: '—',
        status: '—',
        facilityType: '—',
        type: '—',
        org: '—',
        tpl: '—',
        size: '—',
        ownership: '—',
        yearBuilt: '—',
        address: '—',
        city: '—',
        state: '—',
        zip: '—',
        latitude: '—',
        longitude: '—',
        region: '—',
        market: '—',
        marketLead: 'Unassigned',
        regionLead: 'Unassigned',
        coe: 'Unassigned',
        coe2: 'Unassigned',
        executionSystem: '—',
        warehouseSystem: '—',
        labor: '—',
        union: '—',
        supplier: '—',
        totalSqft: '—',
        operationalSqft: '—',
        docks: '—',
        docksEng: '—',
        docksNoEng: '—',
        driveIn: '—',
        bottleLines: '—',
        canLines: '—',
        build1: '—',
        build2: '—',
        bulkSite: '—',
      };

      return fallbackLabels[attr] ?? '—';
    }

    return String(value);
  }, []);

  // Filter locations to only those with valid coordinates
  const locationsWithCoords = useMemo(() =>
    locations.filter(l => l.latitude != null && l.longitude != null && l.latitude !== '' && l.longitude !== ''),
    [locations]
  );

  // Map markers — derived from locations with valid coordinates
  const mapMarkers = useMemo((): MapMarker[] => {
    return locationsWithCoords.map(l => {
      const lat = parseFloat(String(l.latitude));
      const lng = parseFloat(String(l.longitude));
      if (isNaN(lat) || isNaN(lng)) return null;

      return {
        id: l.id,
        latitude: lat,
        longitude: lng,
        label: getLabelForAttribute(l, dataLabelAttribute),
        color: getColorForAttribute(l, showByAttribute),
      };
    }).filter(Boolean) as MapMarker[];
  }, [locationsWithCoords, dataLabelAttribute, showByAttribute, getLabelForAttribute, getColorForAttribute]);

  // Handlers
  const clearAllFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setSearchInput(DEFAULT_FILTERS.search);
    const emptyMoreAttributes = createEmptyMoreAttributesFilters();
    setMoreAttributes(emptyMoreAttributes);
    setAppliedMoreAttributes(emptyMoreAttributes);
    setPage(0);
  }, []);

  const handleBreadcrumbBack = useCallback(() => {
    clearAllFilters();
    setSelectedRows(new Set());
    setSortBy(undefined);
    setSortKey(undefined);
    setSortDirection('default');
    onBack();
  }, [clearAllFilters, onBack]);

  const refetch = useCallback(
    () => fetchLocations(filters, page, pageSize, sortBy, sortDirection, appliedMoreAttributes),
    [appliedMoreAttributes, fetchLocations, filters, page, pageSize, sortBy, sortDirection]
  );

  // Row selection handlers
  const toggleRowSelection = useCallback((id: string) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  }, []);

  const toggleAllRows = useCallback(() => {
    setSelectedRows(prev => {
      const visibleIds = locations.map(location => location.id);
      const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => prev.has(id));
      const next = new Set(prev);

      if (allVisibleSelected) {
        visibleIds.forEach(id => next.delete(id));
      } else {
        visibleIds.forEach(id => next.add(id));
      }

      return next;
    });
  }, [locations]);

  const clearSelection = useCallback(() => setSelectedRows(new Set()), []);

  // Handle filter-by-status from summary strip pills
  const handleFilterByStatus = useCallback((status: string) => {
    setFilters(prev => ({ ...prev, status: [status] }));
    setPage(0);
  }, []);

  const handleSortChange = useCallback((sortKey: string, direction: SortDirection) => {
    const backendColumnName = LOCATION_SORT_FIELD_MAP[sortKey] || sortKey;
    setSortKey(sortKey);
    setSortDirection(direction);
    setSortBy(backendColumnName);
  }, []);

  const handleShowOnMapFromProfile = useCallback(() => {
    if (!selectedLoc) return;
    setWorkspaceView('map');
    setLocationModalMode(null);
    setMapFocusLocationId(selectedLoc.id);
  }, [selectedLoc]);

  const handleEditSuccess = useCallback((msg: string) => {
    toast.showToast({ header: 'Success', subtext: msg, variant: 'success' });
  }, [toast]);

  const handleStatusError = useCallback((msg: string) => {
    toast.showToast({ header: 'Error', subtext: msg, variant: 'error' });
  }, [toast]);

  const handleStatusActivate = useCallback(async (loc: Location) => {
    try {
      await locationApi.updateStatus(loc.id, 'Active');
      refetch();
      dispatch(updateLocationInState({ id: loc.id, status: 'Active' }));
      handleEditSuccess(`${loc.name} has been activated.`);
    } catch (e: any) {
      handleStatusError(e.message ?? 'Failed to activate location.');
    }
  }, [refetch, handleEditSuccess, handleStatusError, dispatch]);

  const handleStatusDeactivate = useCallback(async (loc: Location) => {
    try {
      await locationApi.updateStatus(loc.id, 'Disabled');
      refetch();
      dispatch(updateLocationInState({ id: loc.id, status: 'Disabled' }));
      handleEditSuccess(`${loc.name} has been deactivated.`);
    } catch (e: any) {
      handleStatusError(e.message ?? 'Failed to deactivate location.');
    }
  }, [refetch, handleEditSuccess, handleStatusError, dispatch]);

  const handleStatusIgnore = useCallback(async (loc: Location) => {
    try {
      await locationApi.updateStatus(loc.id, 'Ignored');
      refetch();
      dispatch(updateLocationInState({ id: loc.id, status: 'Ignored' }));
    } catch (e: any) {
      handleStatusError(e.message ?? 'Failed to ignore location.');
    }
  }, [refetch, handleStatusError, dispatch]);

  // Fetch users when detail view opens or the unified location modal is showing profile/edit
  useEffect(() => {
    if ((view === 'detail' || locationModalMode === 'profile' || locationModalMode === 'edit') && selectedLoc?.name) {
      const fetchLocationUsers = async () => {
        setLoadingUsers(true);
        try {
          const userResp = await apiClient(
            apiRef.current,
            `/common/users/users-by-role-ids?roleIds=4,5,6,7,8,9&location=${encodeURIComponent(selectedLoc.name)}`,
            "GET"
          );
          const users = userResp?.users || [];
          
          // Filter out COE, Super User, and Region Leader/Division
          const COEUsers = users.filter((u: any) => 
            u.Role === "COE" || u.Role === "Super User" || u.Role === "Region Leader/Division"
          );
          const COEUserEmails = new Set(COEUsers.map((u: any) => u.UserEmail));
          
          const fieldUsers = users.filter((u: any) => 
            !COEUserEmails.has(u.UserEmail)
          );
          
          setLocationUsers(fieldUsers);
        } catch (error) {
          console.error('Error fetching location users:', error);
          setLocationUsers([]);
        } finally {
          setLoadingUsers(false);
        }
      };
      
      fetchLocationUsers();
    } else if (view !== 'detail' && locationModalMode == null) {
      // Clear users when leaving detail view and no modal open
      setLocationUsers([]);
    }
  }, [view, locationModalMode, selectedLoc?.id, selectedLoc?.name]);

  // Format filter options as FilterOption[] for LocationFilterBar
  const regionFilterOptions = useMemo((): FilterOption[] => 
    regionOptions.map(r => ({ id: r, displayText: r })),
    [regionOptions]
  );

  const marketFilterOptions = useMemo((): FilterOption[] => 
    marketOptions.map(m => ({ id: m, displayText: m })),
    [marketOptions]
  );

  const departmentFilterOptions = useMemo((): FilterOption[] => toFilterOptions(reduxLocTypes), [reduxLocTypes]);
  const orgFilterOptions = useMemo((): FilterOption[] => toFilterOptions(reduxOrgTypes), [reduxOrgTypes]);
  const locationTypeFilterOptions = useMemo((): FilterOption[] => toFilterOptions(reduxLocationFacilityTypes), [reduxLocationFacilityTypes]);

  // Show By options for map coloring
  const showByOptions = useMemo((): SelectOption[] => [
    { label: 'Location Type', value: 'facilityType' },
    { label: 'Union Status', value: 'union' },
    { label: '3PL / OLC', value: 'tpl' },
    { label: 'Warehouse System', value: 'warehouseSystem' },
    { label: 'Ownership', value: 'ownership' },
    { label: 'Labor Model', value: 'labor' },
    { label: 'Region', value: 'region' },
    { label: 'Market', value: 'market' },
  ], []);

  // Data Label options
  const dataLabelOptions = useMemo((): SelectOption[] => [
    ...ALL_COLUMNS.map(column => ({ label: column.label, value: column.key })),
    { label: 'None', value: 'none' },
  ], []);

  // Convert filter state to FilterOption arrays
  const selectedRegionOptions = useMemo((): FilterOption[] =>
    filters.region.map(r => ({ id: r, displayText: r })),
    [filters.region]
  );

  const selectedDepartmentOptions = useMemo((): FilterOption[] =>
    filters.type.map(type => ({ id: type, displayText: type })),
    [filters.type]
  );

  const selectedLocationTypeOptions = useMemo((): FilterOption[] =>
    filters.facilityType.map(facilityType => ({ id: facilityType, displayText: facilityType })),
    [filters.facilityType]
  );

  const selectedMarketOptions = useMemo((): FilterOption[] =>
    filters.market.map(m => ({ id: m, displayText: m })),
    [filters.market]
  );

  const selectedOrgOptions = useMemo((): FilterOption[] =>
    filters.org.map(org => ({ id: org, displayText: org })),
    [filters.org]
  );

  const selectedTplOptions = useMemo((): FilterOption[] =>
    filters.tpl.map(tpl => ({ id: tpl, displayText: tpl })),
    [filters.tpl]
  );

  const selectedUnionStatusOptions = useMemo((): FilterOption[] =>
    filters.unionStatus.map(unionStatus => ({ id: unionStatus, displayText: unionStatus })),
    [filters.unionStatus]
  );

  const selectedSizeOptions = useMemo((): FilterOption[] =>
    filters.size.map(size => ({ id: size, displayText: size })),
    [filters.size]
  );

  // Filter handlers for LocationFilterBar
  const handleRegionChange = useCallback((value: FilterOption | FilterOption[] | null) => {
    const regions = Array.isArray(value) ? value.map(v => String(v.id)) : (value ? [String(value.id)] : []);
    setFilters(prev => ({ ...prev, region: regions }));
  }, []);

  const handleMarketChange = useCallback((value: FilterOption | FilterOption[] | null) => {
    const markets = Array.isArray(value) ? value.map(v => String(v.id)) : (value ? [String(value.id)] : []);
    setFilters(prev => ({ ...prev, market: markets }));
  }, []);

  const handleLocationChange = useCallback((value: FilterOption | FilterOption[] | null) => {
    const locationNames = Array.isArray(value) ? value.map(v => String(v.id)) : (value ? [String(value.id)] : []);
    setFilters(prev => ({ ...prev, locationNames }));
  }, []);

  const handleDepartmentChange = useCallback((value: FilterOption | FilterOption[] | null) => {
    const departments = Array.isArray(value) ? value.map(v => String(v.id)) : (value ? [String(value.id)] : []);
    setFilters(prev => ({ ...prev, type: departments }));
  }, []);

  const handleLocationTypeChange = useCallback((value: FilterOption | FilterOption[] | null) => {
    const types = Array.isArray(value) ? value.map(v => String(v.id)) : (value ? [String(value.id)] : []);
    setFilters(prev => ({ ...prev, facilityType: types }));
  }, []);

  const handleOrgChange = useCallback((value: FilterOption | FilterOption[] | null) => {
    const orgs = Array.isArray(value) ? value.map(v => String(v.id)) : (value ? [String(value.id)] : []);
    setFilters(prev => ({ ...prev, org: orgs }));
  }, []);

  const handleTplChange = useCallback((value: FilterOption | FilterOption[] | null) => {
    const tpl = Array.isArray(value) ? value.map(v => String(v.id)) : (value ? [String(value.id)] : []);
    setFilters(prev => ({ ...prev, tpl }));
  }, []);

  const handleUnionStatusChange = useCallback((value: FilterOption | FilterOption[] | null) => {
    const unionStatus = Array.isArray(value) ? value.map(v => String(v.id)) : (value ? [String(value.id)] : []);
    setFilters(prev => ({ ...prev, unionStatus }));
  }, []);

  const handleSizeChange = useCallback((value: FilterOption | FilterOption[] | null) => {
    const sizes = Array.isArray(value) ? value.map(v => String(v.id)) : (value ? [String(value.id)] : []);
    setFilters(prev => ({ ...prev, size: sizes }));
  }, []);

  const handleStatusChange = useCallback((value: string[]) => {
    setFilters(prev => ({ ...prev, status: value }));
  }, []);

  // ── AdminTableColumn definitions for LocationAdminTable ─────────────────
  // These are the dynamic (non-fixed) columns. Fixed columns (Name, ID, SAP, Status)
  // are built into LocationAdminTable itself.
  const adminTableColumns = useMemo((): AdminTableColumn[] => {
    const all: AdminTableColumn[] = [
      {
        key: 'facilityType',
        label: 'Location Type',
        minWidth: 140,
        getValue: (l) => l.facilityType || '—',
      },
      {
        key: 'type',
        label: 'Department',
        minWidth: 130,
        getValue: (l) => l.type,
      },
      {
        key: 'org',
        label: 'Organization Type',
        minWidth: 150,
        getValue: (l) => l.org,
      },
      {
        key: 'tpl',
        label: '3PL Status',
        minWidth: 110,
        getValue: (l) => l.tpl,
      },
      {
        key: 'size',
        label: 'Location Size',
        minWidth: 120,
        getValue: (l) => l.size,
      },
      {
        key: 'ownership',
        label: 'Ownership',
        minWidth: 120,
        getValue: (l) => l.ownership || '—',
      },
      {
        key: 'yearBuilt',
        label: 'Year Built',
        minWidth: 95,
        numeric: true,
        getValue: (l) => l.yearBuilt || '—',
      },
      {
        key: 'address',
        label: 'Street Address',
        minWidth: 200,
        getValue: (l) => l.address || '—',
      },
      {
        key: 'city',
        label: 'City',
        minWidth: 120,
        getValue: (l) => l.city || '—',
      },
      {
        key: 'state',
        label: 'State / Province',
        minWidth: 130,
        getValue: (l) => l.state || '—',
      },
      {
        key: 'zip',
        label: 'ZIP / Postal Code',
        minWidth: 120,
        getValue: (l) => l.zip || '—',
      },
      {
        key: 'latitude',
        label: 'Latitude',
        minWidth: 95,
        numeric: true,
        getValue: (l) => l.latitude || '—',
      },
      {
        key: 'longitude',
        label: 'Longitude',
        minWidth: 95,
        numeric: true,
        getValue: (l) => l.longitude || '—',
      },
      {
        key: 'region',
        label: 'Region',
        minWidth: 110,
        getValue: (l) => l.region,
      },
      {
        key: 'market',
        label: 'Market',
        minWidth: 110,
        getValue: (l) => l.market,
      },
      {
        key: 'marketLead',
        label: 'Market Lead',
        minWidth: 150,
        getValue: (l) => l.marketLead || 'Unassigned',
      },
      {
        key: 'regionLead',
        label: 'Region Lead',
        minWidth: 150,
        getValue: (l) => l.regionLead || 'Unassigned',
      },
      {
        key: 'coe',
        label: 'Capability Resource',
        minWidth: 160,
        getValue: (l) => l.coe || 'Unassigned',
      },
      {
        key: 'coe2',
        label: 'E2E Resource',
        minWidth: 140,
        getValue: (l) => l.coe2 || 'Unassigned',
      },
      {
        key: 'executionSystem',
        label: 'Execution System',
        minWidth: 150,
        getValue: (l) => l.executionSystem || '—',
      },
      {
        key: 'warehouseSystem',
        label: 'Warehouse System',
        minWidth: 160,
        getValue: (l) => l.warehouseSystem || '—',
      },
      {
        key: 'warehouseStartTime',
        label: 'Warehouse Start Time',
        minWidth: 150,
        getValue: (l) => l.warehouseStartTime || '—',
      },
      {
        key: 'labor',
        label: 'Labor Model',
        minWidth: 130,
        getValue: (l) => l.labor || '—',
      },
      {
        key: 'union',
        label: 'Union Status',
        minWidth: 120,
        getValue: (l) => l.union || '—',
      },
      {
        key: 'supplier',
        label: '3PL Supplier',
        minWidth: 140,
        getValue: (l) => l.supplier || '—',
      },
      {
        key: 'totalSqft',
        label: 'Total Building Sqft',
        minWidth: 135,
        numeric: true,
        getValue: (l) => l.totalSqft || '—',
      },
      {
        key: 'operationalSqft',
        label: 'Operational Sqft',
        minWidth: 135,
        numeric: true,
        getValue: (l) => l.operationalSqft || '—',
      },
      {
        key: 'docks',
        label: 'Docks',
        minWidth: 90,
        numeric: true,
        getValue: (l) => l.docks || '—',
      },
      {
        key: 'docksEng',
        label: 'Docks with ENG Controls',
        minWidth: 160,
        numeric: true,
        getValue: (l) => l.docksEng || '—',
      },
      {
        key: 'docksNoEng',
        label: 'Docks without ENG Controls',
        minWidth: 175,
        numeric: true,
        getValue: (l) => l.docksNoEng || '—',
      },
      {
        key: 'driveIn',
        label: 'Drive-in Bays',
        minWidth: 120,
        numeric: true,
        getValue: (l) => l.driveIn || '—',
      },
      {
        key: 'bottleLines',
        label: 'Bottle Lines',
        minWidth: 115,
        numeric: true,
        getValue: (l) => l.bottleLines || '—',
      },
      {
        key: 'canLines',
        label: 'Can Lines',
        minWidth: 110,
        numeric: true,
        getValue: (l) => l.canLines || '—',
      },
      {
        key: 'build1',
        label: 'Crossdock Build Site 1',
        minWidth: 160,
        getValue: (l) => l.build1 || '—',
      },
      {
        key: 'build2',
        label: 'Crossdock Build Site 2',
        minWidth: 160,
        getValue: (l) => l.build2 || '—',
      },
      {
        key: 'bulkSite',
        label: 'Centralized Bulk Site',
        minWidth: 160,
        getValue: (l) => l.bulkSite || '—',
      },
    ];

    // Filter to only shown columns (but always show all dynamic cols by default)
    return all.filter(c => visibleColumns.includes(c.key));
  }, [visibleColumns]);

  // Export currently visible columns + filtered rows to an .xlsx workbook
  const handleExportLocations = useCallback(async () => {
    const fixedColumns: AdminTableColumn[] = [
      { key: 'id', label: 'Location ID', minWidth: 0, getValue: (l) => l.id },
      { key: 'sap', label: 'SAP ID', minWidth: 0, getValue: (l) => l.sap },
      { key: 'name', label: 'Location Name', minWidth: 0, getValue: (l) => l.name },
      { key: 'status', label: 'Status', minWidth: 0, getValue: (l) => l.status },
    ];
    const exportColumns = [...fixedColumns, ...adminTableColumns];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Locations');
    worksheet.columns = exportColumns.map(col => ({ header: col.label, key: col.key, width: 18 }));
    worksheet.getRow(1).font = { bold: true };

    locations.forEach(location => {
      const row: Record<string, unknown> = {};
      exportColumns.forEach(col => {
        row[col.key] = col.getValue?.(location) ?? '';
      });
      worksheet.addRow(row);
    });

    const today = new Date().toISOString().slice(0, 10);
    try {
      await downloadWorkbook(workbook, `Location_Records_${today}.xlsx`);
    } catch (e: any) {
      handleStatusError(e?.message ?? 'Failed to export locations.');
    }
  }, [adminTableColumns, locations, handleStatusError]);

  // Group-value getter for LocationAdminTable grouping prop
  const getGroupValue = useCallback((loc: Location, key: string): string => {
    const map: Record<string, (l: Location) => string> = {
      region:       l => l.region || 'Unassigned',
      market:       l => l.market || 'Unassigned',
      city:         l => l.city || 'Unassigned',
      state:        l => l.state || 'Unassigned',
      zip:          l => l.zip || 'Unassigned',
      address:      l => l.address || 'Unassigned',
      facilityType: l => l.facilityType || 'Unassigned',
      type:         l => l.type || 'Unassigned',
      org:          l => l.org || 'Unassigned',
      tpl:          l => l.tpl || 'Unassigned',
      size:         l => l.size || 'Unassigned',
      status:       l => l.status,
      ownership:    l => l.ownership || 'Unassigned',
      yearBuilt:    l => l.yearBuilt || 'Unassigned',
      marketLead:   l => l.marketLead || 'Unassigned',
      regionLead:   l => l.regionLead || 'Unassigned',
      coe:          l => l.coe || 'Unassigned',
      coe2:         l => l.coe2 || 'Unassigned',
      executionSystem: l => l.executionSystem || 'Unassigned',
      warehouseSystem: l => l.warehouseSystem || 'Unassigned',
      labor:        l => l.labor || 'Unassigned',
      union:        l => l.union || 'Unassigned',
      supplier:     l => l.supplier || 'Unassigned',
      salesVolume:  l => (l as any).salesVolume || 'Unassigned',
      totalSqft:    l => l.totalSqft || 'Unassigned',
      operationalSqft: l => l.operationalSqft || 'Unassigned',
      docks:        l => l.docks || 'Unassigned',
      docksEng:     l => l.docksEng || 'Unassigned',
      docksNoEng:   l => l.docksNoEng || 'Unassigned',
      driveIn:      l => l.driveIn || 'Unassigned',
      bottleLines:  l => l.bottleLines || 'Unassigned',
      canLines:     l => l.canLines || 'Unassigned',
      build1:       l => l.build1 || 'Unassigned',
      build2:       l => l.build2 || 'Unassigned',
      bulkSite:     l => l.bulkSite || 'Unassigned',
      warehouseStartTime: l => (l as any).warehouseStartTime || 'Unassigned',
      isDuo:        l => (l.isDuo ? 'Enabled' : 'Disabled'),
    };
    return map[key]?.(loc) ?? (loc as any)[key] ?? 'Unassigned';
  }, []);

  // Map grouping presets to field keys
  const getGroupKeysForPreset = useCallback((preset: import('./LocationManagement/components/GroupingBar').GroupingPreset): string[] => {
    const map: Record<string, string[]> = {
      flat:         [],
      region:       ['region'],
      market:       ['market'],
      locationType: ['facilityType'],
    };
    return map[preset] ?? [];
  }, []);

  // Detail view 
  if (view === 'detail' && selectedLoc) {
    return (
      <>
        <LocationDetail
          location={selectedLoc}
          employees={locationUsers}
          onBack={onBack}
          onBackToList={() => setView('list')}
          onEdit={canEditLocations ? () => setLocationModalMode('edit') : undefined}
        />
        <LocationManagementModal
          visible={locationModalMode === 'edit'}
          mode="edit"
          location={selectedLoc}
          employees={locationUsers}
          onClose={() => setLocationModalMode(null)}
          onModeChange={setLocationModalMode}
          onSave={(updated) => {
            refetch();
            setSelectedLoc(updated);
            const SIZE_TO_ENRICHED: Record<string, string> = { Large: 'LARGE', Medium: 'MED', Small: 'SML' };
            dispatch(updateLocationInState({
              id: updated.id,
              name: updated.name,
              locationType: updated.type || null,
              organizationType: updated.org || null,
              is3pl: updated.tpl === 'Yes' ? true : updated.tpl === 'No' ? false : null,
              size: SIZE_TO_ENRICHED[updated.size] ?? updated.size ?? null,
              region: updated.region || null,
              market: updated.market || null,
              status: updated.status || null,
              locSapId: updated.sap || null,
            }));
            handleEditSuccess(`Location '${updated.name}' saved successfully.`);
          }}
        />
      </>
    );
  }

  // List view
  return (
    <ScrollView
      style={[styles.container, isDark && { backgroundColor: colors.background.page }]}
      contentContainerStyle={styles.content}
    >
      <AdminBreadcrumb
        items={[
          { label: 'Configuration Module', onPress: handleBreadcrumbBack },
          { label: 'Location Management' },
        ]}
      />

      {/* EDF Sync Banner — matches .lm-sap-banner from HTML design */}
      {showSyncBanner && requireReviewCount > 0 && (
        <EdfSyncBanner
          reviewCount={requireReviewCount}
          syncTimestamp="Mon 3/24 12:00 AM CST"
          onReviewNow={() => setFilters(prev => ({ ...prev, status: ['Pending Activation', 'Marked for Removal'] }))}
          onDismiss={() => setShowSyncBanner(false)}
        />
      )}

      {/* Page Header — title + subtitle + sync stamp + Add Location CTA */}
      <View style={styles.pageHeader}>
        <View style={styles.pageHeaderLeft}>
          <View
            style={[
              styles.pageIconWrap,
              isDark && { backgroundColor: colors.background.elevated },
            ]}
          >
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" fill={isDark ? colors.brand.primary : AdminColors.blue700} />
              <Circle cx="12" cy="10" r="3" fill="#fff" />
            </Svg>
          </View>
          <View>
            <Text style={[styles.pageTitle, isDark && { color: colors.text.primary }]}>
              Location Management
            </Text>
            <Text style={[styles.pageSubtitle, isDark && { color: colors.text.disabled }]}>
              Manage facility profiles, operating attributes, access, and EDF-synchronized location records.
            </Text>
          </View>
        </View>
        <View style={styles.headerRight}>
          <Text style={[styles.syncStamp, isDark && { color: colors.text.disabled }]}>
            EDF Sync · <Text style={[styles.syncStampDate, isDark && { color: colors.brand.primary }]}>Mon 3/24 12:00 AM CST</Text>
          </Text>
          {canEditLocations && (
            <AdminButton
              label="Add Location"
              variant="primary"
              icon={<PlusIcon />}
              onPress={() => {
                setSelectedLoc(null);
                setLocationModalMode('add');
              }}
            />
          )}
        </View>
      </View>

      {/* Fetch error */}
      {fetchError && <AdminInfoBanner message={fetchError} />}

      {/* Workspace — .lm-admin-workspace */}
      {!fetchError && (
        <View
          style={[
            styles.workspace,
            isDark && { backgroundColor: colors.background.surface, shadowColor: 'transparent' },
          ]}
        >
          {/* Unified Filter Bar — combines view tabs, filters, search, and summary */}
          <View
            style={[
              styles.unifiedFilterBar,
              isDark && { backgroundColor: colors.background.surface, borderBottomColor: colors.border.divider },
            ]}
          >
            <View style={styles.unifiedFilterTopRow}>
              {/* Left: View tabs (TABLE/MAP) */}
              <View style={styles.filterBarLeft}>
                <WorkspaceViewBar
                  activeView={workspaceView}
                  onViewChange={setWorkspaceView}
                  hideFilterSlot
                />
              </View>

              {/* Center: Filter dropdowns + More attributes */}
              <View style={styles.filterBarCenter}>
                {/* Filter dropdowns */}
                <LocationFilterBar
                  searchValue={searchInput}
                  onSearchChange={setSearchInput}
                  onSearchClear={() => {
                    setSearchInput('');
                    setFilters(prev => ({ ...prev, search: '' }));
                  }}
                  regionOptions={regionFilterOptions}
                  marketOptions={marketFilterOptions}
                  departmentOptions={departmentFilterOptions}
                  orgOptions={orgFilterOptions}
                  locationOptions={locationOptions}
                  isLoadingLocations={loadingLocationOptions}
                  locationTypeOptions={locationTypeFilterOptions}
                  selectedRegions={selectedRegionOptions}
                  onRegionChange={handleRegionChange}
                  selectedMarkets={selectedMarketOptions}
                  onMarketChange={handleMarketChange}
                  selectedDepartments={selectedDepartmentOptions}
                  onDepartmentChange={handleDepartmentChange}
                  selectedOrgs={selectedOrgOptions}
                  onOrgChange={handleOrgChange}
                  selectedTpl={selectedTplOptions}
                  onTplChange={handleTplChange}
                  selectedUnionStatuses={selectedUnionStatusOptions}
                  onUnionStatusChange={handleUnionStatusChange}
                  selectedSizes={selectedSizeOptions}
                  onSizeChange={handleSizeChange}
                  selectedLocations={filters.locationNames.map(locationName => ({ id: locationName, displayText: locationName }))}
                  onLocationChange={handleLocationChange}
                  selectedLocationTypes={selectedLocationTypeOptions}
                  onLocationTypeChange={handleLocationTypeChange}
                  selectedStatuses={filters.status}
                  onStatusChange={handleStatusChange}
                  onClearAllFilters={clearAllFilters}
                  isLoadingRegions={loadingFilterOptions}
                  isLoadingMarkets={loadingFilterOptions || loadingMarketOptions}
                  compactMode={true}
                />

                <View style={styles.filterBarActions}>
                  {/* More attributes button */}
                  <View style={styles.moreAttributesButtonWrap}>
                    <MoreAttributesButton
                      label="More attributes"
                      onPress={() => {
                        setMoreAttributes(appliedMoreAttributes);
                        setShowMoreAttributes(true);
                      }}
                    />
                    {moreAttributesCount > 0 && (
                      <View style={styles.moreAttributesBadge}>
                        <Text style={styles.moreAttributesBadgeText}>{moreAttributesCount}</Text>
                      </View>
                    )}
                  </View>

                  {hasActiveFilters && (
                    <TouchableOpacity
                      style={styles.clearAllBtn}
                      onPress={clearAllFilters}
                      activeOpacity={0.7}
                    >
                      <Text style={styles.clearAllBtnText}>Clear all</Text>
                    </TouchableOpacity>
                  )}
                </View>

                {/* Search box */}
                <View
                  style={[
                    styles.unifiedSearchBox,
                    isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border },
                  ]}
                >
                  <Svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <Circle cx="11" cy="11" r="8" stroke={isDark ? colors.text.disabled : AdminColors.textMuted} strokeWidth="2" />
                    <Path d="M21 21l-4.35-4.35" stroke={isDark ? colors.text.disabled : AdminColors.textMuted} strokeWidth="2" strokeLinecap="round" />
                  </Svg>
                  <TextInput
                    style={[styles.unifiedSearchInput, isDark && { color: colors.text.primary }]}
                    placeholder="Search location, ID, SAP ID..."
                    placeholderTextColor={isDark ? colors.text.disabled : AdminColors.textMuted}
                    value={searchInput}
                    onChangeText={setSearchInput}
                  />
                  {searchInput && (
                    <TouchableOpacity onPress={() => {
                      setSearchInput('');
                      setFilters(prev => ({ ...prev, search: '' }));
                    }} style={styles.unifiedSearchClear}>
                      <Svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <Path d="M18 6L6 18M6 6l12 12" stroke={isDark ? colors.text.secondary : AdminColors.textSecondary} strokeWidth="2" strokeLinecap="round" />
                      </Svg>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
              </View>

            {/* Bottom row: summary pills */}
            <View style={styles.filterBarSummaryRow}>
              <SummaryStrip
                total={pagedResult.totalElements}
                filtered={locations.length}
                activeCount={activeCount}
                pendingCount={pendingCount}
                removalCount={removalCount}
                disabledCount={disabledCount}
                loading={loading}
                onFilterByStatus={handleFilterByStatus}
              />
            </View>
          </View>

          {/* TABLE VIEW */}
          {workspaceView === 'table' && (
            <View style={styles.tableCard}>
              {/* Table toolbar — columns picker + export */}
              <LocationTableToolbar
                visibleColumns={visibleColumns}
                onColumnsChange={setVisibleColumns}
                onExport={handleExportLocations}
                totalItems={pagedResult.totalElements}
              />

              {/* Grouping bar */}
              <GroupingBar
                grouping={grouping}
                onChange={setGrouping}
                onExpandAll={() => setGrouping(g => ({ ...g, allExpanded: true }))}
                onCollapseAll={() => setGrouping(g => ({ ...g, allExpanded: false }))}
              />

              {/* Selection bar — visible when rows are selected */}
              <SelectionBar
                selectedCount={selectedVisibleCount}
                canEdit={canEditLocations}
                onEdit={() => {
                  const loc = selectedLocationsInView[0];
                  if (loc) { setSelectedLoc(loc); setLocationModalMode('edit'); }
                }}
                onBulkEdit={() => {
                  setLocationModalMode('bulk');
                }}
                onClear={clearSelection}
              />

              {/* LocationAdminTable — one-to-one HTML design copy */}
              <LocationAdminTable
                data={locations}
                columns={adminTableColumns}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
                selectedIds={selectedRows}
                onToggleRow={toggleRowSelection}
                onToggleAll={toggleAllRows}
                onRowPress={(loc) => { setSelectedLoc(loc); setLocationModalMode('profile'); }}
                onActivate={handleStatusActivate}
                onDeactivate={handleStatusDeactivate}
                onRevert={(loc) => { setRevertLoc(loc); setShowRevert(true); }}
                onIgnore={handleStatusIgnore}
                currentPage={page}
                totalPages={pagedResult.totalPages}
                onPageChange={setPage}
                groupBy={grouping.preset !== 'flat' || grouping.levels.length > 0
                  ? (grouping.levels.length > 0 ? grouping.levels : getGroupKeysForPreset(grouping.preset))
                  : []}
                getGroupValue={getGroupValue}
                allExpanded={grouping.allExpanded}
                loading={loading}
                footerText={loading ? 'Loading locations…' : undefined}
              />
            </View>
          )}

          {/* MAP VIEW */}
          {workspaceView === 'map' && (
            <LocationMapView
              locations={locations}
              locationsWithCoords={locationsWithCoords}
              focusedLocationId={mapFocusLocationId}
              showByAttribute={showByAttribute}
              onShowByAttributeChange={setShowByAttribute}
              dataLabelAttribute={dataLabelAttribute}
              onDataLabelAttributeChange={setDataLabelAttribute}
              getColorForAttribute={getColorForAttribute}
              getLabelForAttribute={getLabelForAttribute}
              mapMarkers={mapMarkers}
              showByOptions={showByOptions}
              dataLabelOptions={dataLabelOptions}
              onMarkerPress={(id) => {
                const loc = locations.find(l => l.id === id);
                if (loc) { setSelectedLoc(loc); setLocationModalMode('profile'); }
              }}
            />
          )}
        </View>
      )}

      {/* Location Form Modal — unified add/edit/bulk */}
      <LocationManagementModal
        visible={locationModalMode != null}
        mode={locationModalMode ?? 'profile'}
        location={locationModalMode === 'add' ? null : selectedLoc}
        onShowOnMap={handleShowOnMapFromProfile}
        selectedLocations={locationModalMode === 'bulk' ? selectedLocationsInView : []}
        employees={locationUsers}
        onClose={() => {
          setLocationModalMode(null);
        }}
        onModeChange={setLocationModalMode}
          hideEditLocation={!canEditLocations}
        onDuoToggle={(value) => {
          if (!selectedLoc) return;
          setSelectedLoc(prev => (prev ? { ...prev, isDuo: value } : prev));
          dispatch(updateLocationInState({ id: selectedLoc.id, isDuo: value }));
          setPagedResult(prev => ({
            ...prev,
            items: prev.items.map(item => (item.id === selectedLoc.id ? { ...item, isDuo: value } : item)),
          }));
        }}
        onSave={(updated) => {
          setPage(0);
          refetch();
          const SIZE_TO_ENRICHED: Record<string, string> = { Large: 'LARGE', Medium: 'MED', Small: 'SML' };
          dispatch(updateLocationInState({
            id: updated.id,
            name: updated.name,
            locationType: updated.type || null,
            organizationType: updated.org || null,
            is3pl: updated.tpl === 'Yes' ? true : updated.tpl === 'No' ? false : null,
            isDuo: updated.isDuo ?? null,
            size: SIZE_TO_ENRICHED[updated.size] ?? updated.size ?? null,
            region: updated.region || null,
            market: updated.market || null,
            status: updated.status || null,
            locSapId: updated.sap || null,
          }));
          const successMessage = locationModalMode === 'add'
            ? `Location '${updated.name}' created successfully.`
            : `Location '${updated.name}' saved successfully.`;
          handleEditSuccess(successMessage);
        }}
        onBulkApply={(updatedIds, failedCount) => {
          clearSelection();
          refetch();
          const msg = failedCount > 0
            ? `${updatedIds.length} location${updatedIds.length !== 1 ? 's' : ''} updated; ${failedCount} failed.`
            : `${updatedIds.length} location${updatedIds.length !== 1 ? 's' : ''} updated successfully.`;
          handleEditSuccess(msg);
        }}
        onToast={(message) => {
          handleStatusError(message);
        }}
      />
      <RevertModal
        visible={showRevert}
        locationName={revertLoc?.name ?? ''}
        onClose={() => setShowRevert(false)}
        onConfirm={async (reason) => {
          if (revertLoc) {
            try {
              await locationApi.updateStatus(revertLoc.id, 'Active', reason);
              refetch();
              dispatch(updateLocationInState({ id: revertLoc.id, status: 'Active' }));
            } catch { /* non-critical */ }
          }
          setShowRevert(false);
        }}
      />
      <MoreAttributesDrawer
        visible={showMoreAttributes}
        onClose={() => {
          setMoreAttributes(appliedMoreAttributes);
          setShowMoreAttributes(false);
        }}
        value={moreAttributes}
        onChange={setMoreAttributes}
        onApply={() => {
          setAppliedMoreAttributes(moreAttributes);
          setPage(0);
        }}
        onReset={() => {
          const emptyMoreAttributes = createEmptyMoreAttributesFilters();
          setMoreAttributes(emptyMoreAttributes);
          setAppliedMoreAttributes(emptyMoreAttributes);
          setPage(0);
        }}
        ownershipOptions={ownershipOptions}
        coeResourceOptions={coeResourceOptions}
        secondaryCoeResourceOptions={secondaryCoeResourceOptions}
        regionLeadOptions={regionLeadOptions}
        stateProvinceOptions={stateProvinceOptions}
        warehouseSystemOptions={warehouseSystemOptions}
        laborOptions={laborOptions}
        supplierOptions={supplierOptions}
        ownershipSearchOptions={createMoreAttributeSearchOptions('ownership')}
        regionLeadSearchOptions={createMoreAttributeSearchOptions('regionLead')}
        stateProvinceSearchOptions={createMoreAttributeSearchOptions('stateProvince')}
        warehouseSystemSearchOptions={createMoreAttributeSearchOptions('warehouseSystem')}
        laborSearchOptions={createMoreAttributeSearchOptions('laborModel')}
        supplierSearchOptions={createMoreAttributeSearchOptions('tplSupplier')}
        buildSite1Options={buildSite1Options}
        buildSite2Options={buildSite2Options}
        bulkSiteOptions={bulkSiteOptions}
        buildSite1SearchOptions={createMoreAttributeSearchOptions('crossdockBuildSite1')}
        buildSite2SearchOptions={createMoreAttributeSearchOptions('crossdockBuildSite2')}
        bulkSiteSearchOptions={createMoreAttributeSearchOptions('centralizedBulkSite')}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: AdminColors.bgPage },
  content: { padding: 28, paddingBottom: 60 },

  // Page header
  pageHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 14,
    flexWrap: 'wrap',
    gap: 12,
  },
  pageHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  pageIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 9,
    backgroundColor: AdminColors.blue50,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
    lineHeight: 32,
  },
  pageSubtitle: {
    fontSize: 13,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
    marginTop: 2,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingTop: 8,
  },
  syncStamp: {
    fontSize: 12,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
  },
  syncStampDate: {
    fontWeight: '700',
    color: AdminColors.blue700,
    fontFamily: FontFamily.bold,
  },

  // Workspace container
  workspace: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    shadowColor: AdminColors.blue600,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.08,
    shadowRadius: 24,
    elevation: 2,
    overflow: 'hidden',
  },

  // Table card
  tableCard: {
    overflow: 'hidden',
  },

  // Scrollable table container
  tableScrollContainer: {
    height: 600,
    backgroundColor: AdminColors.bgSurface,
  },

  // Table cells
  idCell: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: AdminColors.textPrimary,
  },
  idCellLink: {
    fontSize: 12,
    fontWeight: '700',
    color: AdminColors.blue800,
    fontFamily: FontFamily.bold,
  },
  cell: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: AdminColors.textPrimary,
  },
  iconBtn: {
    width: 29,
    height: 29,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
  },

  // Row checkbox
  checkboxCell: {
    width: 42,
    height: 42,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkbox: {
    width: 16,
    height: 16,
    borderRadius: 3,
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: AdminColors.bgSurface,
  },
  checkboxChecked: {
    backgroundColor: AdminColors.blue700,
    borderColor: AdminColors.blue700,
  },

  // Empty state
  emptyState: {
    paddingVertical: 52,
    alignItems: 'center',
    gap: 14,
  },
  emptyTitle: {
    fontSize: 14,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
  },

  // Legacy styles kept for compatibility
  surface: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
  },
  filterBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 10,
    flexWrap: 'wrap',
    gap: 6,
  },
  toolbarLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  tableSum: {
    fontSize: 12,
    fontWeight: '500',
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.medium,
  },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    paddingHorizontal: 8,
    height: 30,
    minWidth: 200,
    backgroundColor: AdminColors.bgSurface,
  },
  searchInput: {
    flex: 1,
    fontSize: 12,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    height: 28,
    padding: 0,
  },
  loadingWrap: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  loadingTxt: {
    fontSize: 14,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
  },
  pagination: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: AdminColors.gray25,
  },
  pageInfo: {
    fontSize: 13,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.medium,
  },

  // Unified filter bar
  unifiedFilterBar: {
    flexDirection: 'column',
    alignItems: 'stretch',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray25,
    backgroundColor: AdminColors.bgSurface,
  },
  unifiedFilterTopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
    flexWrap: 'nowrap',
  },
  filterBarLeft: {
    flexShrink: 0,
    alignSelf: 'flex-start',
  },
  filterBarCenter: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  filterBarRight: {
    flexShrink: 0,
  },
  filterBarActions: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    flexShrink: 0,
    marginTop: 20,
  },
  moreAttributesButtonWrap: {
    position: 'relative',
    alignSelf: 'flex-start',
    marginTop: 0,
  },
  moreAttributesBadge: {
    position: 'absolute',
    top: -6,
    right: -6,
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    paddingHorizontal: 5,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: AdminColors.blue800,
  },
  moreAttributesBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: AdminColors.textOnDark,
    fontFamily: FontFamily.bold,
  },
  clearAllBtn: {
    paddingHorizontal: 4,
    paddingVertical: 4,
    minHeight: 36,
    justifyContent: 'center',
  },
  clearAllBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: AdminColors.blue800,
    fontFamily: FontFamily.medium,
  },
  filterBarSummaryRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 10,
  },
  unifiedSearchBox: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    paddingHorizontal: 10,
    height: 36,
    backgroundColor: AdminColors.bgSurface,
    maxWidth: 400,
    alignSelf: 'flex-start',
    marginTop: 22,
  },
  unifiedSearchInput: {
    flex: 1,
    fontSize: 12,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    padding: 0,
    height: 36,
  },
  unifiedSearchClear: {
    padding: 4,
  },

});

export default LocationManagementScreen;
